import { type ReactNode } from 'react'
import cx from 'classnames'
import {
  type MotionStyle,
  m,
  useMotionTemplate,
  useTransform,
} from 'motion/react'
import styles from './ContentFader.module.css'
import { useElementSize, useScrollOffsets } from '@/hooks/element-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'

export interface ContentFaderProps {
  children?: ReactNode

  /**
   * If set, determines how far outside the scroll boundary children will paint before
   * being completely hidden by the fader.
   *
   * The value must be assignable to the CSS `margin` and `padding` properties.
   */
  clipMargin?:
    | number
    | string
    | {
        x?: number | string
        y?: number | string
        top?: number | string
        bottom?: number | string
        left?: number | string
        right?: number | string
      }

  /**
   * Whether to show the native scrollbar.
   * Default is `false`.
   */
  scrollbar?: boolean

  /**
   * At what scroll offset to start the fade effect.
   *
   * @default 0
   */
  fadeStart?: number

  /**
   * At what scroll offset to finish the fade effect.
   *
   * @default 20
   */
  fadeEnd?: number
}

/**
 * Fades content that scrolls horizontally.
 */
export function ContentFader({
  children,
  clipMargin,
  scrollbar = false,
  fadeStart = 0,
  fadeEnd = 20,
}: ContentFaderProps) {
  const computedClipMargin = getComputedClipMargin(clipMargin)

  const {
    scrollXStart,
    scrollXEnd,
    connectRef: connectScrollRef,
    computeScrollValues,
  } = useScrollOffsets()

  const { connectRef: connectSizeRef } = useElementSize({
    onChange: computeScrollValues,
  })

  const fadeStartOpacity = useTransform(
    scrollXStart,
    [fadeStart, fadeEnd],
    [0, 100],
  )
  const fadeEndOpacity = useTransform(
    scrollXEnd,
    [fadeStart, fadeEnd],
    [0, 100],
  )

  const maskImage = useMotionTemplate`linear-gradient(
    to right,
    color-mix(in srgb, white, transparent ${fadeStartOpacity}%) 0%,
    white 10%,
    white 90%,
    color-mix(in srgb, white, transparent ${fadeEndOpacity}%)
  )`

  return (
    <m.div
      ref={useComposedRefs(connectSizeRef, connectScrollRef)}
      className={cx(styles.root, !scrollbar && styles.disableScrollbar)}
      style={
        {
          maskImage,
          '--fader-clip-margin-top': computedClipMargin.top,
          '--fader-clip-margin-right': computedClipMargin.right,
          '--fader-clip-margin-bottom': computedClipMargin.bottom,
          '--fader-clip-margin-left': computedClipMargin.left,
        } as MotionStyle
      }
    >
      <div className={styles.content}>{children}</div>
    </m.div>
  )
}

interface ComputedClipMargin {
  top?: string
  right?: string
  bottom?: string
  left?: string
}

function getComputedClipMargin(
  margin: ContentFaderProps['clipMargin'],
): ComputedClipMargin {
  if (!margin) {
    return {
      top: undefined,
      right: undefined,
      bottom: undefined,
      left: undefined,
    }
  }

  if (typeof margin === 'string') {
    return {
      top: margin,
      right: margin,
      bottom: margin,
      left: margin,
    }
  }

  if (typeof margin === 'number') {
    const px = `${margin}px`
    return {
      top: px,
      right: px,
      bottom: px,
      left: px,
    }
  }

  return {
    top: normalizeMarginValue(margin.top) ?? normalizeMarginValue(margin.y),
    right: normalizeMarginValue(margin.right) ?? normalizeMarginValue(margin.x),
    bottom:
      normalizeMarginValue(margin.bottom) ?? normalizeMarginValue(margin.y),
    left: normalizeMarginValue(margin.left) ?? normalizeMarginValue(margin.x),
  }
}

function normalizeMarginValue(value?: string | number) {
  if (value === undefined) {
    return undefined
  }

  return typeof value === 'number' ? `${value}px` : value
}
